import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  BookOpenIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import { apiClient } from '@/lib/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CombinedStats, TTSJob, Book, HealthCheck, TTSWorkerHealth } from '@/types/api';
import { getJobTitle, getJobVoice, parseJobMetadata } from '@/types/api';

const DAYS_TO_SHOW = 7;

interface ActivityPoint {
  name: string;
  jobs: number;
  books: number;
}

function buildWeeklyActivity(jobs: TTSJob[], books: Book[]): ActivityPoint[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  start.setDate(today.getDate() - (DAYS_TO_SHOW - 1));

  const jobCounts: Record<string, number> = {};
  const bookCounts: Record<string, number> = {};

  jobs.forEach((job) => {
    const ts = job.finished_at || job.created_at;
    if (!ts) return;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0);
    if (d < start || d > today) return;
    const key = d.toISOString().slice(0, 10);
    if (job.status === 'completed' || job.status === 'failed') {
      jobCounts[key] = (jobCounts[key] || 0) + 1;
    }
  });

  books.forEach((book) => {
    if (book.status !== 'PUBLISHED') return;
    const ts = book.completed_at || book.started_at || null;
    if (!ts) return;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return;
    d.setHours(0, 0, 0, 0);
    if (d < start || d > today) return;
    const key = d.toISOString().slice(0, 10);
    bookCounts[key] = (bookCounts[key] || 0) + 1;
  });

  const points: ActivityPoint[] = [];

  for (let i = DAYS_TO_SHOW - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    d.setHours(0, 0, 0, 0);
    const key = d.toISOString().slice(0, 10);

    points.push({
      name: d.toLocaleDateString('en-US', { weekday: 'short' }),
      jobs: jobCounts[key] || 0,
      books: bookCounts[key] || 0,
    });
  }

  return points;
}

function computeActiveTenantStats(jobs: TTSJob[]): {
  lastHour: number;
  today: number;
  last7Days: number;
  last30Days: number;
} {
  const now = new Date();
  const hourAgo = new Date(now.getTime() - 60 * 60 * 1000);
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const hourSet = new Set<string>();
  const todaySet = new Set<string>();
  const weekSet = new Set<string>();
  const monthSet = new Set<string>();

  jobs.forEach((job) => {
    const metadata = parseJobMetadata(job);
    const tenantId = metadata?.tenant_id || job.tenant_id || null;
    if (!tenantId) return;

    const ts = job.updated_at || job.created_at;
    if (!ts) return;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return;

    if (d >= monthAgo) {
      monthSet.add(tenantId);
      if (d >= weekAgo) {
        weekSet.add(tenantId);
      }
      if (d >= dayAgo) {
        todaySet.add(tenantId);
      }
      if (d >= hourAgo) {
        hourSet.add(tenantId);
      }
    }
  });

  return {
    lastHour: hourSet.size,
    today: todaySet.size,
    last7Days: weekSet.size,
    last30Days: monthSet.size,
  };
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
    case 'PUBLISHED':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'running':
    case 'processing':
    case 'PROCESSING':
      return <Badge variant="primary" dot>Running</Badge>;
    case 'pending':
    case 'PENDING':
      return <Badge variant="warning" dot>Pending</Badge>;
    case 'failed':
    case 'FAILED':
      return <Badge variant="danger" dot>Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
};

// Pipeline Card Component - Kampus Media Brand Style
interface PipelineCardProps {
  title: string;
  value: number;
  change: number;
  changeLabel: string;
  icon: React.ReactNode;
  color: 'orange' | 'purple' | 'emerald' | 'rose';
  progress?: number;
  isLoading?: boolean;
}

function PipelineCard({ title, value, change, changeLabel, icon, color, progress, isLoading }: PipelineCardProps) {
  const colorClasses = {
    orange: {
      bg: 'from-primary-500 to-orange-600',
      glow: 'shadow-primary-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    purple: {
      bg: 'from-secondary-500 to-violet-600',
      glow: 'shadow-secondary-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    emerald: {
      bg: 'from-emerald-500 to-teal-600',
      glow: 'shadow-emerald-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    rose: {
      bg: 'from-rose-500 to-pink-600',
      glow: 'shadow-rose-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
  };

  const TrendIcon = change < 0 ? ArrowTrendingDownIcon : ArrowTrendingUpIcon;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color].bg} p-6 shadow-xl ${colorClasses[color].glow} transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${isLoading ? 'opacity-40 animate-pulse' : ''}`}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id={`grid-${color}`} width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill={`url(#grid-${color})`} />
        </svg>
      </div>

      {/* Decorative circles */}
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10 blur-xl" />
      <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/5" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl ${colorClasses[color].icon}`}>
            {icon}
          </div>
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm">
            <TrendIcon className="w-3.5 h-3.5 text-white" />
            <span className="text-xs font-semibold text-white">{Math.abs(change)}%</span>
          </div>
        </div>

        <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
        <p className="text-4xl font-bold text-white mb-2 tracking-tight">{value.toLocaleString()}</p>
        <p className="text-white/60 text-xs">{changeLabel}</p>

        {/* Progress bar for running jobs */}
        {progress !== undefined && (
          <div className="mt-4">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className={`h-full ${colorClasses[color].bar} rounded-full transition-all duration-500`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  // Real data state
  const [stats, setStats] = useState<CombinedStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<TTSJob[]>([]);
  const [recentBooks, setRecentBooks] = useState<Book[]>([]);
  const [jobsHistory, setJobsHistory] = useState<TTSJob[]>([]);
  const [booksForActivity, setBooksForActivity] = useState<Book[]>([]);
  const [activityData, setActivityData] = useState<ActivityPoint[]>([]);
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [workerHealth, setWorkerHealth] = useState<TTSWorkerHealth | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [booksLoading, setBooksLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [booksError, setBooksError] = useState<string | null>(null);

  // Calculate running progress (average of running jobs)
  const runningProgress = recentJobs
    .filter(j => j.status === 'running' || j.status === 'processing')
    .reduce((sum, j) => sum + j.progress, 0) / Math.max(1, recentJobs.filter(j => j.status === 'running' || j.status === 'processing').length) || 0;

  // Fetch combined stats from backend
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await apiClient.get<CombinedStats>('tts/queue/stats');
      setStats(response);
      setStatsError(null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setStatsError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch system / worker health status (used for system status bar & infra chips)
  const fetchHealth = useCallback(async () => {
    try {
      try {
        const healthResponse = await apiClient.get<HealthCheck>('public/health');
        setHealth(healthResponse);
      } catch (err) {
        console.error('Failed to fetch unified health:', err);
        setHealth(null);
      }

      try {
        const workerHealthResponse = await apiClient.get<TTSWorkerHealth>('tts/health');
        setWorkerHealth(workerHealthResponse);
      } catch (err) {
        console.error('Failed to fetch TTS worker health:', err);
        setWorkerHealth(null);
      }
    } catch (err) {
      console.error('Unexpected error while fetching health:', err);
      setHealth(null);
      setWorkerHealth(null);
    }
  }, []);

  // Fetch jobs history from backend (used for both recent jobs + weekly activity)
  const fetchJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const response = await apiClient.get<TTSJob[]>('tts/jobs/history?limit=200');
      setJobsHistory(response);
      setRecentJobs(response.slice(0, 5));
      setJobsError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setJobsError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  // Fetch recent books from distribution backend
  const fetchBooks = useCallback(async () => {
    try {
      setBooksLoading(true);
      const response = await apiClient.get<{ items: Book[] } | Book[]>('distribution/books?pageSize=100');
      // Handle both paginated response and array response
      const books = Array.isArray(response) ? response : response.items || [];
      setBooksForActivity(books);
      setRecentBooks(books.slice(0, 5));
      setBooksError(null);
    } catch (err) {
      console.error('Failed to fetch books:', err);
      // Don't show error for auth issues - just show empty state
      if (err instanceof Error && err.message.includes('401')) {
        setBooksError('Login required for distribution data');
      } else {
        setBooksError(err instanceof Error ? err.message : 'Failed to fetch books');
      }
    } finally {
      setBooksLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchStats();
    fetchJobs();
    fetchBooks();
    fetchHealth();
  }, [fetchStats, fetchJobs, fetchBooks, fetchHealth]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats();
      fetchJobs();
      fetchBooks();
      fetchHealth();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchJobs, fetchBooks, fetchHealth]);

  // Recompute weekly activity whenever source data changes
  useEffect(() => {
    setActivityData(buildWeeklyActivity(jobsHistory, booksForActivity));
  }, [jobsHistory, booksForActivity]);

  // Extract stats values with fallbacks
  const pendingCount = stats?.queue_stats.pending_jobs ?? 0;
  const runningCount = stats?.queue_stats.processing_jobs ?? 0;
  const completedCount = stats?.job_metrics.completed_jobs ?? 0;
  const failedCount = stats?.job_metrics.failed_jobs ?? 0;
  const successRate = stats?.job_metrics.success_rate ?? 0;

  const activeTenantStats = computeActiveTenantStats(jobsHistory);

  const distributionStatus = health?.checks.distribution_worker;
  const distributionIsOk = distributionStatus === 'ok';
  const distributionIsError = distributionStatus === 'error';

  const r2Service = workerHealth?.services?.r2_storage;
  const r2Status = r2Service?.status;
  const r2IsConnected = !!r2Service;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Welcome back! Here's an overview of your pipelines.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/tts/upload">
            <Button
              leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
              className="bg-primary-500 hover:bg-primary-600 focus:ring-primary-500"
            >
              New Upload
            </Button>
          </Link>
          <Link to="/distribution/books">
            <Button variant="outline" leftIcon={<BookOpenIcon className="w-4 h-4" />}>
              Manage Books
            </Button>
          </Link>
        </div>
      </div>

      {/* TTS Pipeline Section - Connected to Real Backend */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">TTS Pipeline</h2>
          <Badge variant="primary" className="animate-pulse-soft">Live</Badge>
          {statsError && (
            <span className="text-xs text-red-500">Connection Error</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PipelineCard
            title="Pending Jobs"
            value={pendingCount}
            change={0}
            changeLabel="in queue"
            icon={<ClockIcon className="w-6 h-6" />}
            color="orange"
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Running"
            value={runningCount}
            change={0}
            changeLabel="processing now"
            icon={<PlayIcon className="w-6 h-6" />}
            color="purple"
            progress={runningProgress || 50}
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Completed"
            value={completedCount}
            change={Math.round(successRate)}
            changeLabel="success rate"
            icon={<CheckCircleIcon className="w-6 h-6" />}
            color="emerald"
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Failed"
            value={failedCount}
            change={0}
            changeLabel="needs attention"
            icon={<ExclamationTriangleIcon className="w-6 h-6" />}
            color="rose"
            isLoading={statsLoading}
          />
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart - CONNECTED */}
        <WidgetWrapper isConnected className="lg:col-span-2">
          <Card>
            <CardHeader title="Weekly Activity" subtitle="Jobs processed and books published" />
            <CardBody>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityData}>
                    <defs>
                      <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F58220" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#F58220" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorBooks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8E7CC3" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#8E7CC3" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="jobs"
                      stroke="#F58220"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorJobs)"
                      name="TTS Jobs"
                    />
                    <Area
                      type="monotone"
                      dataKey="books"
                      stroke="#8E7CC3"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorBooks)"
                      name="Books"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>

        {/* Active Tenants - CONNECTED (derived from job activity) */}
        <WidgetWrapper isConnected>
          <Card>
            <CardHeader title="Active Tenants" subtitle="Based on recent TTS job activity" />
            <CardBody>
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-primary-500">
                  {activeTenantStats.lastHour}
                </p>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                  Active tenants (last 60 minutes)
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-primary-500">
                    {activeTenantStats.today}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Today</p>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-primary-500">
                    {activeTenantStats.last7Days}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Last 7 days</p>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-primary-500">
                    {activeTenantStats.last30Days}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Last 30 days</p>
                </div>
              </div>

              <Link to="/tts/telemetry">
                <Button className="w-full mt-6" variant="primary">
                  Details
                </Button>
              </Link>
            </CardBody>
          </Card>
        </WidgetWrapper>
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent TTS Jobs - CONNECTED */}
        <LoadingOverlay isLoading={jobsLoading} loadingOpacity={40}>
          <Card>
            <CardHeader
              title="Recent Jobs"
              subtitle="Latest TTS conversions"
              action={
                <Link to="/tts/jobs">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Job</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Voice</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentJobs.length === 0 && !jobsLoading ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                        {jobsError ? (
                          <span className="text-red-500">{jobsError}</span>
                        ) : (
                          'No recent jobs'
                        )}
                      </td>
                    </tr>
                  ) : (
                    recentJobs.slice(0, 5).map((job) => (
                      <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/tts/jobs/${job.job_id}`} className="hover:text-primary-500">
                            <p className="font-medium text-slate-800 dark:text-white">{getJobTitle(job)}</p>
                            <p className="text-xs text-slate-400">{job.job_id}</p>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {getJobVoice(job)}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(job.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </LoadingOverlay>

        {/* Recent Books - CONNECTED */}
        <LoadingOverlay isLoading={booksLoading} loadingOpacity={40}>
          <Card>
            <CardHeader
              title="Recent Books"
              subtitle="Latest distribution activity"
              action={
                <Link to="/distribution/books">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Book</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Publisher</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentBooks.length === 0 && !booksLoading ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                        {booksError ? (
                          <span className="text-red-500">{booksError}</span>
                        ) : (
                          'No recent books'
                        )}
                      </td>
                    </tr>
                  ) : (
                    recentBooks.slice(0, 5).map((book) => (
                      <tr key={book.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/distribution/books/${book.id}`} className="hover:text-primary-500">
                            <p className="font-medium text-slate-800 dark:text-white">{book.title}</p>
                            <p className="text-xs text-slate-400">{book.isbn}</p>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{book.publisher_name || '--'}</td>
                        <td className="px-4 py-3">{getStatusBadge(book.status || 'PENDING')}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </LoadingOverlay>
      </div>

      {/* System Health - Connected to backend status */}
      <div className="relative mt-8">
        {/* Decorative separator */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent" />

        <div className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 py-3 rounded-xl bg-slate-100/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-center gap-2">
              <CpuChipIcon className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">System Status</span>
            </div>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              {/* TTS Worker */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    stats?.system_status === 'healthy'
                      ? 'bg-emerald-500 animate-pulse'
                      : 'bg-yellow-500'
                  }`}
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">TTS Worker</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">{runningCount} jobs</span>
              </div>

              {/* Distribution Worker - Connected via /public/health */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    !distributionStatus
                      ? 'bg-slate-400'
                      : distributionIsOk
                      ? 'bg-emerald-500 animate-pulse'
                      : distributionIsError
                      ? 'bg-danger-500'
                      : 'bg-warning-500'
                  }`}
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">Distribution</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {distributionStatus
                    ? distributionIsOk
                      ? 'Healthy'
                      : distributionIsError
                      ? 'Error'
                      : 'Degraded'
                    : 'Unknown'}
                </span>
              </div>

              {/* R2 Storage - Connected via /tts/health */}
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    !r2Status
                      ? 'bg-slate-400'
                      : r2Status === 'ok' || r2Status === 'healthy'
                      ? 'bg-emerald-500 animate-pulse'
                      : r2Status === 'error' || r2Status === 'unhealthy'
                      ? 'bg-danger-500'
                      : 'bg-warning-500'
                  }`}
                />
                <span className="text-sm text-slate-600 dark:text-slate-300">R2 Storage</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  {r2IsConnected ? r2Status || 'Unknown' : '--'}
                </span>
              </div>

              <Link to="/tts/health" className="text-xs text-primary-500 hover:text-primary-600 font-medium">
                View Details →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
